export type NovedadItem = {
  date: string;
  description: string;
  href: string;
  tags: string[];
  title: string;
};

export const NOVEDADES_URL = "https://docs.lapyme.com.ar/novedades";
export const NOVEDADES_RSS_URL = `${NOVEDADES_URL}/rss.xml`;

function decodeXml(value: string) {
  return value
    .replaceAll("&amp;", "&")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&quot;", '"')
    .replaceAll("&apos;", "'");
}

function getRssTag(source: string, tag: string) {
  const escapedTag = tag.replace(":", "\\:");
  const match = source.match(
    new RegExp(
      `<${escapedTag}[^>]*>\\s*(?:<!\\[CDATA\\[)?([\\s\\S]*?)(?:\\]\\]>)?\\s*</${escapedTag}>`
    )
  );

  return decodeXml(match?.[1]?.trim() ?? "");
}

function getRssTags(source: string, tag: string) {
  const escapedTag = tag.replace(":", "\\:");
  const pattern = new RegExp(
    `<${escapedTag}[^>]*>\\s*(?:<!\\[CDATA\\[)?([\\s\\S]*?)(?:\\]\\]>)?\\s*</${escapedTag}>`,
    "g"
  );

  return Array.from(source.matchAll(pattern))
    .map((match) => decodeXml(match[1]?.trim() ?? ""))
    .filter(Boolean);
}

function stripRssHtml(value: string) {
  return decodeXml(
    value
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

function excerpt(value: string, maxLength = 140) {
  const clean = stripRssHtml(value);

  if (clean.length <= maxLength) {
    return clean;
  }

  return `${clean.slice(0, maxLength).trimEnd()}...`;
}

export function parseNovedadesRss(source: string, limit = 3): NovedadItem[] {
  return Array.from(source.matchAll(/<item>([\s\S]*?)<\/item>/g))
    .slice(0, limit)
    .map((match) => {
      const item = match[1] ?? "";
      const content = getRssTag(item, "content:encoded");
      const firstHeading = content.match(/<h2[^>]*>([\s\S]*?)<\/h2>/)?.[1];
      const firstParagraph = content.match(/<p[^>]*>([\s\S]*?)<\/p>/)?.[1];

      return {
        date: getRssTag(item, "title"),
        description: excerpt(firstParagraph ?? content),
        href: getRssTag(item, "link") || NOVEDADES_URL,
        tags: getRssTags(item, "category"),
        title: stripRssHtml(
          firstHeading ?? firstParagraph ?? getRssTag(item, "title")
        ),
      };
    })
    .filter((update) => update.date && update.title && update.description);
}

export function getLatestNovedadesVersion(
  novedades: NovedadItem[]
): string | null {
  return novedades[0]?.href ?? null;
}

export function getUnreadNovedadesVersions(
  novedades: NovedadItem[],
  acknowledgedVersion: string | null | undefined
): string[] {
  if (novedades.length === 0) {
    return [];
  }

  if (!acknowledgedVersion) {
    return novedades.map((novedad) => novedad.href);
  }

  const acknowledgedIndex = novedades.findIndex(
    (novedad) => novedad.href === acknowledgedVersion
  );

  if (acknowledgedIndex === 0) {
    return [];
  }

  if (acknowledgedIndex === -1) {
    return novedades.map((novedad) => novedad.href);
  }

  return novedades.slice(0, acknowledgedIndex).map((novedad) => novedad.href);
}
